import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { incidentTypes } from './libraryData';
import { useIncidents, useOrgs } from '../../hooks/useLibraryData';
import './Library.css';

// Filter toggle button
function FilterButton({ label, count, isOpen, onClick }) {
  const hasSelection = count > 0;
  return (
    <button
      className={`hf-dropdown-btn ${hasSelection ? 'has-selection' : ''} ${isOpen ? 'is-open' : ''}`}
      onClick={onClick}
    >
      {label} {count > 0 && `(${count})`} <span className="hf-arrow"></span>
    </button>
  );
}

// Inline expand panel
function ExpandPanel({ options, selected, onToggle }) {
  return (
    <div className="hf-expand-panel">
      {options.map(opt => (
        <span
          key={opt}
          className={`hf-panel-option ${selected.includes(opt) ? 'selected' : ''}`}
          onClick={() => onToggle(opt)}
        >
          {opt}
        </span>
      ))}
    </div>
  );
}

const IncidentsPage = () => {
  const { incidents: incidentsData } = useIncidents();
  const { orgs: orgsData } = useOrgs();
  const getOrgById = (id) => orgsData.find(org => org.id === id);

  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilters, setCountryFilters] = useState([]);
  const [activeTypeTags, setActiveTypeTags] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (name) => setOpenPanel(prev => prev === name ? null : name);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
  };

  const toggleTypeTag = (type) => {
    setActiveTypeTags(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleCountry = (country) => {
    setCountryFilters(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCountryFilters([]);
    setActiveTypeTags([]);
  };

  const hasActiveFilters = searchQuery.trim() !== '' || countryFilters.length > 0 || activeTypeTags.length > 0;

  const filteredIncidents = useMemo(() => {
    let results = [...incidentsData];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(incident =>
        incident.title.toLowerCase().includes(query) ||
        incident.description.toLowerCase().includes(query) ||
        incident.institution.toLowerCase().includes(query)
      );
    }

    if (countryFilters.length > 0) {
      results = results.filter(incident => countryFilters.includes(incident.country));
    }

    if (activeTypeTags.length > 0) {
      results = results.filter(incident =>
        activeTypeTags.some(type => incident.tags.includes(type))
      );
    }

    results.sort((a, b) => new Date(b.date) - new Date(a.date));
    return results;
  }, [searchQuery, countryFilters, activeTypeTags, incidentsData]);

  const countries = useMemo(() => {
    const countrySet = new Set();
    incidentsData.forEach(i => { if (i.country) countrySet.add(i.country); });
    return Array.from(countrySet).sort();
  }, [incidentsData]);

  return (
    <>
      <Helmet>
        <title>Incidents - Hasbara Tracker</title>
        <meta name='description' content='Documented cases where Israeli lobbying and propaganda campaigns have influenced institutions and suppressed speech' />
      </Helmet>
      <Header />

      <main className='library-page'>
        {/* Header */}
        <section className='library-header'>
          <h1 className='library-page-title'>Incidents</h1>
          <p className='library-intro'>
            Documented cases where Israeli state-affiliated lobbying and propaganda campaigns have influenced institutions, suppressed speech, or reshaped public discourse.
          </p>
          <p className='library-stats-line'>
            {incidentsData.length} incidents documented across {countries.length} countries
          </p>
        </section>

        {/* Search bar */}
        <input
          type='text'
          placeholder='Search incidents...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='hf-search'
        />

        {/* Filter bar */}
        <div className='hf-filter-bar'>
          <div className='hf-button-row'>
            <FilterButton label='Type' count={activeTypeTags.length} isOpen={openPanel === 'type'} onClick={() => togglePanel('type')} />
            <FilterButton label='Location' count={countryFilters.length} isOpen={openPanel === 'location'} onClick={() => togglePanel('location')} />
            {hasActiveFilters && <button className='hf-clear-all' onClick={clearFilters}>Clear all</button>}
          </div>
          {openPanel === 'type' && <ExpandPanel options={incidentTypes} selected={activeTypeTags} onToggle={toggleTypeTag} />}
          {openPanel === 'location' && <ExpandPanel options={countries} selected={countryFilters} onToggle={toggleCountry} />}
        </div>

        <p className='library-results-count'>
          {filteredIncidents.length} incident{filteredIncidents.length !== 1 ? 's' : ''}
          {hasActiveFilters ? ` (filtered from ${incidentsData.length})` : ''}
        </p>

        {/* Incidents List */}
        <div className='library-items-list'>
          {filteredIncidents.length > 0 ? (
            filteredIncidents.map(incident => (
              <Link
                key={incident.slug}
                to={`/incidents/${incident.slug}`}
                className='library-incident-item'
              >
                <span className='library-incident-date'>{formatDate(incident.date)}</span>
                <div className='library-item-content'>
                  <div className='library-item-header'>
                    <h3 className='library-item-title'>{incident.title}</h3>
                  </div>
                  <p className='library-item-description'>{incident.description}</p>
                  <div className='library-item-footer'>
                    <span className='library-incident-meta-line'>
                      {incident.country}
                      {incident.institution && <> · {incident.institution}</>}
                    </span>
                    {incident.pressuredBy && incident.pressuredBy.length > 0 && (
                      <span className='library-incident-pressure'>
                        · Pressured by: {incident.pressuredBy.map(orgId => {
                          const org = getOrgById(orgId);
                          return org ? org.name : orgId;
                        }).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className='library-no-results'>
              <p>No incidents match your filters.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default IncidentsPage;
